
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

export function SMSPreferencesTable() {
  const queryClient = useQueryClient();
  const [editStates, setEditStates] = useState<Record<number, { phoneNumber: string; receiveSms: boolean }>>({});

  const { data: preferences = [], isLoading } = useQuery({
    queryKey: ["sms-preferences"],
    queryFn: async () => {
      const res = await fetch("/api/admin/sms-preferences");
      if (!res.ok) throw new Error("Failed to fetch SMS preferences");
      const data = await res.json();
      // Initialize edit states with fetched data
      setEditStates(data.reduce((acc: any, pref: any) => ({
        ...acc,
        [pref.userId]: {
          phoneNumber: pref.phoneNumber || "",
          receiveSms: pref.receiveSms || false
        }
      }), {}));
      return data;
    },
  });

  const updatePreference = useMutation({
    mutationFn: async (data: { userId: number; receiveSms: boolean; phoneNumber?: string }) => {
      const res = await fetch(`/api/admin/sms-preferences/${data.userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update SMS preferences");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sms-preferences"]);
      toast({ title: "SMS preferences updated successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error updating SMS preferences",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handlePhoneNumberChange = (userId: number, value: string) => {
    setEditStates(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        phoneNumber: value
      }
    }));
  };

  const handleSmsToggle = (userId: number, checked: boolean) => {
    const newState = {
      ...editStates[userId],
      receiveSms: checked
    };
    setEditStates(prev => ({
      ...prev,
      [userId]: newState
    }));
    updatePreference.mutate({
      userId,
      receiveSms: checked,
      phoneNumber: newState.phoneNumber
    });
  };

  const handleSave = (userId: number) => {
    const state = editStates[userId];
    if (state) {
      updatePreference.mutate({
        userId,
        receiveSms: state.receiveSms,
        phoneNumber: state.phoneNumber
      });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Phone Number</TableHead>
              <TableHead>Receive SMS</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {preferences.map((pref) => (
              <TableRow key={pref.userId}>
                <TableCell>{pref.userEmail}</TableCell>
                <TableCell>
                  <Input
                    value={editStates[pref.userId]?.phoneNumber || ""}
                    onChange={(e) => handlePhoneNumberChange(pref.userId, e.target.value)}
                    placeholder="Enter phone number"
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={editStates[pref.userId]?.receiveSms || false}
                    onCheckedChange={(checked) => handleSmsToggle(pref.userId, checked)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSave(pref.userId)}
                  >
                    Save
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
